import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import type { ObservationsShadowResponse, ShadowDay } from "../api/types";
import { useAuth } from "../lib/auth/useAuth";
import { useRole } from "../lib/auth/useRole";
import { useSelfUrl } from "../lib/auth/useSelfUrl";
import {
  resolveRepoProjects,
  type ChantierSource,
  type ShadowRange,
} from "../lib/chantiers";
import { getDisplayName } from "../lib/session";
import {
  normalizeWorkerRef,
  sortByWorkerLabel,
  workerLabel,
} from "../lib/team";
import { useImputationRules } from "./useImputationRules";
import { useManagerTeam } from "./useManagerTeam";
import { observationsShadowQuery } from "./useObservationsShadow";
import { useProjects } from "./usePm";

/** Les observations d'un collègue sont auditées à chaque lecture : pas de
 * relecture avant un quart d'heure. */
const TEAM_STALE_MS = 15 * 60_000;

/** Jours de toutes les lectures, chacune bornée à sa plage ; null tant
 * qu'une lecture manque. */
function joinedDays(
  reads: UseQueryResult<ObservationsShadowResponse>[],
  ranges: ShadowRange[],
): ShadowDay[] | null {
  if (!reads.every((q) => q.data)) return null;
  return ranges.flatMap((r, i) =>
    reads[i]!.data!.days.filter((d) => d.date >= r.from && d.date <= r.to),
  );
}

/** Observations shadow du salarié connecté sur `ranges` (31 jours au plus
 * chacune), et pour un manager ou un admin celles de son équipe, chacun sous
 * ses propres règles d'imputation. Un collègue illisible est écarté et
 * signalé dans `unreadable`. */
export function useChantierSources(ranges: ShadowRange[]) {
  const { user } = useAuth();
  const selfUrl = useSelfUrl();
  const role = useRole();
  const teamView = role === "manager" || role === "admin";
  const team = useManagerTeam(teamView);
  const colleagues = teamView
    ? sortByWorkerLabel(
        (team.data?.workers ?? []).filter(
          (w) =>
            !selfUrl ||
            normalizeWorkerRef(w.worker_url) !== normalizeWorkerRef(selfUrl),
        ),
      )
    : [];
  const own = useQueries({
    queries: ranges.map((r) => observationsShadowQuery(selfUrl, r.from, r.to)),
  });
  const theirs = useQueries({
    queries: colleagues.flatMap((w) =>
      ranges.map((r) => ({
        ...observationsShadowQuery(w.worker_url, r.from, r.to),
        staleTime: TEAM_STALE_MS,
      })),
    ),
  });
  const rules = useImputationRules();
  const projects = useProjects();
  const projectOf = (workerUrl: string) =>
    resolveRepoProjects(
      rules.data?.rules ?? [],
      projects.data?.items ?? [],
      workerUrl,
    );
  const sources: ChantierSource[] = [];
  const unreadable: string[] = [];
  const ownDays = joinedDays(own, ranges);
  if (ownDays) {
    sources.push({
      workerUrl: teamView ? selfUrl : "",
      name: getDisplayName(user),
      days: ownDays,
      projectOf: projectOf(selfUrl),
    });
    colleagues.forEach((w, i) => {
      const reads = theirs.slice(i * ranges.length, (i + 1) * ranges.length);
      const days = joinedDays(reads, ranges);
      if (days) {
        sources.push({
          workerUrl: w.worker_url,
          name: workerLabel(w),
          days,
          projectOf: projectOf(w.worker_url),
        });
      } else if (reads.some((q) => q.isError)) unreadable.push(workerLabel(w));
    });
  }
  return {
    selfUrl,
    teamView,
    sources,
    unreadable,
    loading:
      own.some((q) => q.isLoading) ||
      rules.isLoading ||
      projects.isLoading ||
      (teamView && team.isLoading) ||
      theirs.some((q) => q.isLoading),
    /** Les heures du salarié connecté ne sont pas lisibles. */
    failed: ownDays === null,
    teamFailed: teamView && team.isError,
    retry: () => own.forEach((q) => void q.refetch()),
  };
}
